import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  X, Calendar, Users, Star, Target,
  CheckCircle2, AlertCircle, Plus, Minus
} from 'lucide-react';
import { employeeData } from '../data/mockData';
import ModalWrapper from '../../../components/ModalWrapper';

const NewReviewForm = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    employeeId: '',
    reviewerId: '',
    period: '',
    scores: {
      technical: 0,
      communication: 0,
      teamwork: 0,
      leadership: 0,
      innovation: 0
    },
    feedback: {
      strengths: [''],
      improvements: ['']
    },
    goals: [{
      title: '',
      description: '',
      deadline: '',
      status: 'Not Started'
    }]
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleScoreChange = (metric, value) => {
    setFormData(prev => ({
      ...prev,
      scores: {
        ...prev.scores,
        [metric]: parseFloat(value)
      }
    }));
  };

  const handleFeedbackChange = (type, index, value) => {
    setFormData(prev => ({
      ...prev,
      feedback: {
        ...prev.feedback,
        [type]: prev.feedback[type].map((item, i) => 
          i === index ? value : item
        )
      }
    }));
  };

  const addFeedbackItem = (type) => {
    setFormData(prev => ({
      ...prev,
      feedback: {
        ...prev.feedback,
        [type]: [...prev.feedback[type], '']
      }
    }));
  };

  const removeFeedbackItem = (type, index) => {
    setFormData(prev => ({
      ...prev,
      feedback: {
        ...prev.feedback,
        [type]: prev.feedback[type].filter((_, i) => i !== index)
      }
    }));
  };

  const handleGoalChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.map((goal, i) => 
        i === index ? { ...goal, [field]: value } : goal
      )
    }));
  };

  const addGoal = () => {
    setFormData(prev => ({
      ...prev,
      goals: [...prev.goals, {
        title: '',
        description: '',
        deadline: '',
        status: 'Not Started'
      }]
    }));
  };

  const removeGoal = (index) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.employeeId) newErrors.employeeId = 'Employee is required';
    if (!formData.reviewerId) newErrors.reviewerId = 'Reviewer is required';
    if (!formData.period) newErrors.period = 'Review period is required';
    
    // Validate scores
    Object.entries(formData.scores).forEach(([metric, score]) => {
      if (score < 1 || score > 5) {
        newErrors[`score_${metric}`] = 'Score must be between 1 and 5';
      }
    });

    // Validate goals
    formData.goals.forEach((goal, index) => {
      if (!goal.title) newErrors[`goal_${index}_title`] = 'Goal title is required';
      if (!goal.deadline) newErrors[`goal_${index}_deadline`] = 'Deadline is required';
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      onSubmit(formData);
      setSubmitStatus('success');
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      setSubmitStatus('error');
      setErrors({ submit: 'Failed to create review. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrapper isOpen={true} onClose={onClose}>
      <div className="flex flex-col max-h-[calc(100vh-4rem)]">
        <div className="flex-shrink-0 p-6 border-b bg-white">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">New Performance Review</h2>
              <p className="text-sm text-gray-500">Create a new performance review</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee *
                  </label>
                  <select
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                      errors.employeeId ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Employee</option>
                    {employeeData.employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} - {emp.position}
                      </option>
                    ))}
                  </select>
                  {errors.employeeId && (
                    <p className="mt-1 text-sm text-red-500">{errors.employeeId}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reviewer *
                  </label>
                  <select
                    name="reviewerId"
                    value={formData.reviewerId}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                      errors.reviewerId ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Reviewer</option>
                    {employeeData.employees
                      .filter(emp => emp.id !== formData.employeeId)
                      .map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name} - {emp.position}
                        </option>
                      ))}
                  </select>
                  {errors.reviewerId && (
                    <p className="mt-1 text-sm text-red-500">{errors.reviewerId}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Review Period *
                  </label>
                  <select
                    name="period"
                    value={formData.period}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                      errors.period ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Period</option>
                    <option value="2024-Q1">2024 Q1</option>
                    <option value="2024-Q2">2024 Q2</option>
                    <option value="2024-Q3">2024 Q3</option>
                    <option value="2024-Q4">2024 Q4</option>
                  </select>
                  {errors.period && (
                    <p className="mt-1 text-sm text-red-500">{errors.period}</p>
                  )}
                </div>
              </div>

              {/* Performance Scores */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Scores</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(formData.scores).map(([metric, score]) => (
                    <div key={metric}>
                      <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                        {metric.replace(/([A-Z])/g, ' $1').trim()}
                      </label>
                      <select
                        value={score}
                        onChange={(e) => handleScoreChange(metric, e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                          errors[`score_${metric}`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="0">Select Score</option>
                        <option value="1">1 - Poor</option>
                        <option value="2">2 - Below Average</option>
                        <option value="3">3 - Average</option>
                        <option value="4">4 - Above Average</option>
                        <option value="5">5 - Excellent</option>
                      </select>
                      {errors[`score_${metric}`] && (
                        <p className="mt-1 text-sm text-red-500">{errors[`score_${metric}`]}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Feedback */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Feedback</h3>
                
                {/* Strengths */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">Strengths</label>
                    <button
                      type="button"
                      onClick={() => addFeedbackItem('strengths')}
                      className="text-sm text-indigo-600 hover:text-indigo-700"
                    >
                      Add Strength
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.feedback.strengths.map((strength, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={strength}
                          onChange={(e) => handleFeedbackChange('strengths', index, e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          placeholder="Enter strength"
                        />
                        {formData.feedback.strengths.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeFeedbackItem('strengths', index)}
                            className="p-2 text-gray-400 hover:text-red-500"
                          >
                            <Minus className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Areas for Improvement */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">Areas for Improvement</label>
                    <button
                      type="button"
                      onClick={() => addFeedbackItem('improvements')}
                      className="text-sm text-indigo-600 hover:text-indigo-700"
                    >
                      Add Area
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.feedback.improvements.map((improvement, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={improvement}
                          onChange={(e) => handleFeedbackChange('improvements', index, e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          placeholder="Enter area for improvement"
                        />
                        {formData.feedback.improvements.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeFeedbackItem('improvements', index)}
                            className="p-2 text-gray-400 hover:text-red-500"
                          >
                            <Minus className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Goals */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Goals</h3>
                  <button
                    type="button"
                    onClick={addGoal}
                    className="text-sm text-indigo-600 hover:text-indigo-700"
                  >
                    Add Goal
                  </button>
                </div>
                <div className="space-y-4">
                  {formData.goals.map((goal, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Goal Title *
                          </label>
                          <input
                            type="text"
                            value={goal.title}
                            onChange={(e) => handleGoalChange(index, 'title', e.target.value)}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                              errors[`goal_${index}_title`] ? 'border-red-500' : 'border-gray-300'
                            }`}
                          />
                          {errors[`goal_${index}_title`] && (
                            <p className="mt-1 text-sm text-red-500">{errors[`goal_${index}_title`]}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Deadline *
                          </label>
                          <input
                            type="date"
                            value={goal.deadline}
                            onChange={(e) => handleGoalChange(index, 'deadline', e.target.value)}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                              errors[`goal_${index}_deadline`] ? 'border-red-500' : 'border-gray-300'
                            }`}
                          />
                          {errors[`goal_${index}_deadline`] && (
                            <p className="mt-1 text-sm text-red-500">{errors[`goal_${index}_deadline`]}</p>
                          )}
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            value={goal.description}
                            onChange={(e) => handleGoalChange(index, 'description', e.target.value)}
                            rows={2}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                      {formData.goals.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeGoal(index)}
                          className="mt-2 text-sm text-red-600 hover:text-red-700"
                        >
                          Remove Goal
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Status */}
              {submitStatus && (
                <div className={`p-4 rounded-lg ${
                  submitStatus === 'success' 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-red-50 text-red-700'
                }`}>
                  <div className="flex items-center gap-2">
                    {submitStatus === 'success' ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <AlertCircle className="w-5 h-5" />
                    )}
                    <span>
                      {submitStatus === 'success'
                        ? 'Review created successfully!'
                        : 'Failed to create review. Please try again.'}
                    </span>
                  </div>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                    transition-colors flex items-center gap-2 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    'Create Review'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="flex-shrink-0 p-6 border-t bg-gray-50">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Create Review
            </button>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default NewReviewForm; 